class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();

    this.storageKey = 'laatukaluste:recently-viewed-products';
    this.dataElement = this.querySelector('[data-recently-viewed-products-data]');
    this.grid = this.querySelector('[data-recently-viewed-products-grid]');
  }

  connectedCallback() {
    if (!this.dataElement || !this.grid) return;

    const config = this.parseConfig();
    if (!config) return;

    this.config = config;
    this.layout = this.dataset.layout;

    const currentProduct = config.currentProduct;
    const items = this.uniqueItems(this.readItems()).filter((item) => this.isFresh(item, config));
    const visibleItems = items
      .filter((item) => !currentProduct || item.handle !== currentProduct.handle)
      .slice(0, config.limit);

    this.destroySlider();

    if (visibleItems.length > 0) {
      this.renderItems(visibleItems);
      this.setupLayout();
      this.hidden = false;
    } else {
      this.grid.replaceChildren();
      this.hidden = true;
    }

    if (currentProduct) {
      this.saveCurrentProduct(currentProduct, items, config);
    } else if (items.length !== this.readItems().length) {
      this.writeItems(items);
    }
  }

  parseConfig() {
    try {
      return JSON.parse(this.dataElement.textContent);
    } catch (error) {
      return null;
    }
  }

  readItems() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter((item) => item && item.handle && item.title && item.url)
        .map((item) => ({
          ...item,
          viewedAt: Number(item.viewedAt) || Date.now()
        }));
    } catch (error) {
      return [];
    }
  }

  writeItems(items) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(items));
    } catch (error) {
      return;
    }
  }

  uniqueItems(items) {
    const handles = new Set();
    return items.filter((item) => {
      if (handles.has(item.handle)) return false;
      handles.add(item.handle);
      return true;
    });
  }

  isFresh(item, config) {
    if (!config.enableProductLifetime) return true;

    const ttl = Math.max(Number(config.ttlHours) || 0, 0);
    if (ttl === 0) return false;

    return Number(item.viewedAt) + ttl * 60 * 60 * 1000 > Date.now();
  }

  saveCurrentProduct(currentProduct, items, config) {
    const now = Date.now();
    const productToSave = {
      ...currentProduct,
      viewedAt: now
    };

    if (config.enableProductLifetime) {
      productToSave.expiresAt = now + Math.max(Number(config.ttlHours) || 0, 0) * 60 * 60 * 1000;
    }

    const updatedItems = this.uniqueItems([
      productToSave,
      ...items.filter((item) => item.handle !== currentProduct.handle)
    ])
      .filter((item) => this.isFresh(item, config))
      .slice(0, config.storageLimit);

    this.writeItems(updatedItems);
  }

  renderItems(items) {
    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      fragment.append(this.createCard(item));
    });

    this.grid.replaceChildren(fragment);
  }

  setupLayout() {
    if (this.layout !== 'slider') {
      this.grid.classList.remove('recently-viewed-products__swiper-wrapper');
      delete this.dataset.sliderReady;
      delete this.dataset.sliderHasNavigation;
      return;
    }

    this.grid.classList.add('recently-viewed-products__swiper-wrapper');
    delete this.dataset.sliderReady;
    this.createSlider();
  }

  createSlider() {
    if (!window.Swiper) {
      this.dataset.sliderReady = 'true';
      return;
    }

    const enableNavigation = this.grid.children.length > 4;
    const slideCount = this.grid.children.length;
    const shouldLoop = slideCount > 4;

    this.dataset.sliderHasNavigation = enableNavigation ? 'true' : 'false';

    this.swiper = new window.Swiper(this.querySelector('[data-recently-viewed-products-viewport]'), {
      slidesPerView: 1.9,
      spaceBetween: 10,
      observer: true,
      observeParents: true,
      watchOverflow: true,
      loop: shouldLoop,
      centeredSlides: shouldLoop,
      navigation: {
        nextEl: this.querySelector('[data-recently-viewed-products-next]'),
        prevEl: this.querySelector('[data-recently-viewed-products-previous]'),
        enabled: enableNavigation
      },
      on: {
        init: () => {
          this.dataset.sliderReady = 'true';
        },
        afterInit: (swiper) => {
          swiper.update();
          this.dataset.sliderReady = 'true';
        }
      },
      breakpoints: {
        799: {
            slidesPerView: 3.9
        }
      }
    });
  }

  destroySlider() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  getStyleValue(property) {
    return window.getComputedStyle(this).getPropertyValue(property).trim();
  }

  createCard(item) {
    const card = document.createElement('a');
    card.className = 'recently-viewed-products__card swiper-slide';
    card.href = item.url;
    card.draggable = false;

    const imageWrapper = document.createElement('div');
    imageWrapper.className = 'recently-viewed-products__image-wrapper';

    if (item.id && item.variantId) {
      imageWrapper.append(this.createWishlistButton(item));
    }

    if (item.image) {
      const image = document.createElement('img');
      image.className = 'recently-viewed-products__image';
      image.src = item.image;
      image.alt = item.imageAlt || item.title;
      image.loading = 'lazy';
      image.fecthpriority = 'low';
      image.draggable = false;
      imageWrapper.append(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'recently-viewed-products__placeholder';
      imageWrapper.append(placeholder);
    }

    const info = document.createElement('div');
    info.className = 'recently-viewed-products__info';

    const title = document.createElement('span');
    title.className = 'recently-viewed-products__title';
    title.textContent = item.title;
    info.append(title);

    const hasSalePrice = Boolean(item.compareAtPrice);
    const price = document.createElement('span');
    price.className = 'recently-viewed-products__price';
    price.textContent = item.price || '';

    if (hasSalePrice) {
      const compareAtPrice = document.createElement('span');
      compareAtPrice.className = 'recently-viewed-products__compare-price';
      compareAtPrice.textContent = item.compareAtPrice;
      price.append(compareAtPrice);
    }

    info.append(price);
    card.append(imageWrapper, info);

    return card;
  }

  createWishlistButton(item) {
    const wishlist = document.createElement('span');
    wishlist.className = 'smartwishlist customize_wishlist recently-viewed-products__wishlist';
    wishlist.dataset.product = item.id;
    wishlist.dataset.variant = item.variantId;

    return wishlist;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
